import css from "../Queues.module.css";

import { useCallback, useEffect, useState } from "react";
import Client from "../../../../components/distributed_systems/Client/Client";
import Server from "../../../../components/distributed_systems/Server/Server";
import System from "../../../../components/distributed_systems/System/System";
import Track from "../../../../components/distributed_systems/Track/Track";
import Queue from "../../../../components/distributed_systems/Queue/Queue";
import { v4 as uuid } from "uuid";

const WithFIFOQueue = ({ isPlaying }) => {
  const QUEUE_LIMIT = 4;

  const [requests, setRequests] = useState([]);
  const [history, setHistory] = useState([]);

  const serveNext = (list) => {
    const isServerAvailable = !list.some(
      (r) => r.status === "queue-server" || r.status === "server-processing",
    );
    if (!isServerAvailable) return list;

    const index = list.findIndex((r) => r.status === "queued");
    if (index === -1) return list;

    const newList = [...list];
    newList[index] = { ...newList[index], status: "queue-server" };
    return newList;
  };

  const sendRequest = () => {
    const request = {
      id: uuid(),
      createdAt: Date.now(),
      loadSize: 1,
      timeoutSeconds: 6,
      status: "client-queue",
      networkSpeed: 500,
    };

    setRequests((old) => [request, ...old]);
  };

  useEffect(() => {
    if (!isPlaying) return;
    const intervalId = setInterval(() => {
      sendRequest();
    }, 500);
    return () => clearInterval(intervalId);
  }, [isPlaying]);

  const removeRequest = useCallback((request) => {
    setRequests((old) => old.filter((r) => r.id !== request.id));

    const hasTimedOut =
      !!request.createdAt &&
      !!request.timeoutSeconds &&
      Date.now() - request.createdAt >= request.timeoutSeconds * 1000;

    const finalRequest = hasTimedOut
      ? { ...request, status: "timed-out" }
      : request;

    setHistory((old) => [...old, finalRequest]);
  }, []);

  const enterQueue = useCallback((request) => {
    setRequests((old) => {
      const queued = old.filter((r) =>
        ["queued", "queuing"].includes(r.status),
      );
      const queueLength = queued.length;
      const index = old.findIndex((req) => req.id === request.id);
      if (index === -1) return old;
      const newRequests = [...old];
      if (queueLength >= QUEUE_LIMIT) {
        newRequests[index] = {
          ...newRequests[index],
          status: "queue-rejected",
        };
        return newRequests;
      }

      newRequests[index] = {
        ...newRequests[index],
        status: "queuing",
        info: {
          from: -1,
          to: 0,
        },
      };

      queued.forEach((req) => {
        if (req.id === request.id) return;
        const shiftedIdx = newRequests.findIndex((r) => r.id === req.id);
        if (shiftedIdx === -1) return;
        const prevTo = req.info?.to ?? 0;
        newRequests[shiftedIdx] = {
          ...newRequests[shiftedIdx],
          status: "queuing",
          info: {
            from: prevTo,
            to: prevTo + 1,
          },
        };
      });

      return serveNext(newRequests);
    });
  }, []);

  const setQueuePosition = useCallback((request) => {
    setRequests((old) => {
      const queueArray = old.filter((req) =>
        ["queued", "queuing"].includes(req.status),
      );
      const currentIndex = queueArray.findIndex((req) => req.id === request.id);
      console.log(`currentIndex: ${JSON.stringify(currentIndex)}`);
      if (currentIndex === -1) return old;

      const newRequests = old.map((req) => {
        if (req.id !== request.id) return req;
        if (!["queued", "queuing"].includes(req.status)) return req;

        return {
          ...req,
          status: "queued",
          info: {
            from: req.info?.to ?? currentIndex,
            to: currentIndex,
          },
        };
      });

      return serveNext(newRequests);
    });
  }, []);

  const enterServer = useCallback((request) => {
    setRequests((old) => {
      const isAvailable = !old.some((r) => r.status === "server-processing");

      const index = old.findIndex((req) => req.id === request.id);
      if (index === -1) return old;

      const newRequests = [...old];
      newRequests[index] = {
        ...newRequests[index],
        status: isAvailable ? "server-processing" : "server-rejected",
      };

      return newRequests;
    });
  }, []);

  const serveRequest = useCallback(async (request) => {
    setRequests((old) => {
      const index = old.findIndex((req) => req.id === request.id);
      if (index === -1) return old;

      const queuedRequests = old.filter((req) => req.status === "queued");
      const firstInLine = queuedRequests[0];
      const stillQueued = queuedRequests.slice(1); // everyone but firstInLine

      const newRequests = old.map((req) => {
        if (req.id === request.id) {
          return { ...req, status: "server-processed" };
        }
        if (firstInLine && req.id === firstInLine.id) {
          return req; // leave as "queued"; serveNext will promote it
        }
        const stillQueuedIndex = stillQueued.findIndex((r) => r.id === req.id);
        if (stillQueuedIndex !== -1) {
          return {
            ...req,
            status: "queuing",
            info: {
              from: req.info?.to ?? stillQueuedIndex,
              to: stillQueuedIndex, // real position, not a decrement
            },
          };
        }
        return req;
      });

      return serveNext(newRequests);
    });
  }, []);

  const processed = history.filter(
    (req) => req.status === "server-processed",
  ).length;
  const rejected = history.filter(
    (req) => req.status === "queue-rejected",
  ).length;
  const timedOut = history.filter((req) => req.status === "timed-out").length;

  return (
    <div className={css.demo}>
      <i>LAST IN, FIRST OUT</i>
      <System>
        <Client onClick={sendRequest} type="mobile" />
        <Track
          requests={requests}
          onComplete={enterQueue}
          statuses={["client-queue", "queue-rejected"]}
          removeRequest={removeRequest}
        />
        <Queue
          limit={QUEUE_LIMIT}
          requests={requests}
          statuses={["queuing", "queued"]}
          onComplete={setQueuePosition}
        />
        <Track
          requests={requests}
          onComplete={enterServer}
          statuses={["queue-server", "server-rejected"]}
          removeRequest={removeRequest}
        />
        <Server
          request={
            requests.filter(
              (request) => request.status === "server-processing",
            )[0]
          }
          onComplete={serveRequest}
        />
        <Track
          requests={requests}
          statuses={["server-processed"]}
          removeRequest={removeRequest}
        />
      </System>
      <div className={css.info}>
        <div className={css.stats_bar}>
          <div
            className={`${css.stat_bar} ${css.stat_bar_accepted}`}
            style={{ width: `${(processed / history.length || 0.33) * 100}%` }}
          />
          <div
            className={`${css.stat_bar} ${css.stat_bar_timeout}`}
            style={{
              width: `${history.length > 0 ? (timedOut / history.length) * 100 : 33}%`,
            }}
          />
        </div>
        <div className={css.stat_labels}>
          <div className={css.stat}>
            <div
              className={`${css.stat_icon_container} ${css.stat_icon_container_accepted}`}
            >
              <img
                className={css.stat_icon}
                src="https://storage.googleapis.com/squidslippers-media/icons/check.png"
                alt="check"
              />
            </div>
            <strong className={css.stat_number}>
              {Math.round((processed / history.length) * 100) || 0}
              <span className={css.percent}>%</span>
            </strong>
          </div>
          <div className={css.stat}>
            <div
              className={`${css.stat_icon_container} ${css.stat_icon_container_timeout}`}
            >
              <img
                className={css.stat_icon}
                src="https://storage.googleapis.com/squidslippers-media/icons/no.png"
                alt="x"
              />
            </div>
            <strong className={css.stat_number}>
              {Math.round((timedOut / history.length) * 100) || 0}
              <span className={css.percent}>%</span>
            </strong>
          </div>
          <div className={css.stat}>
            <div
              className={`${css.stat_icon_container} ${css.stat_icon_container_rejected}`}
            >
              <img
                className={css.stat_icon}
                src="https://storage.googleapis.com/squidslippers-media/icons/x.png"
                alt="x"
              />
            </div>
            <strong className={css.stat_number}>
              {Math.round((rejected / history.length) * 100) || 0}
              <span className={css.percent}>%</span>
            </strong>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithFIFOQueue;
